import { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { fetchJson, sitePath } from '../lib/api';

const DIFFICULTY_ORDER = ['Easy', 'Medium', 'Hard', 'Very Hard'];

export default function PuzzleListPage() {
  const { typeSlug } = useParams();
  const navigate = useNavigate();
  const [puzzles, setPuzzles] = useState([]);
  const [type, setType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDifficulty, setSelectedDifficulty] = useState('All');

  useEffect(() => {
    setLoading(true);
    setSelectedDifficulty('All');
    fetchJson(sitePath(`/catalog/${typeSlug}`))
      .then((json) => {
        setType(json.data?.type || null);
        setPuzzles(json.data?.puzzles || []);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [typeSlug]);

  const difficultyOptions = useMemo(() => {
    const values = Array.from(
      new Set(
        puzzles
          .map((puzzle) => puzzle.difficulty)
          .filter(Boolean)
      )
    );

    return values.sort(
      (left, right) =>
        DIFFICULTY_ORDER.indexOf(left) - DIFFICULTY_ORDER.indexOf(right)
    );
  }, [puzzles]);

  const filteredPuzzles = useMemo(() => {
    if (selectedDifficulty === 'All') {
      return puzzles;
    }

    return puzzles.filter((puzzle) => puzzle.difficulty === selectedDifficulty);
  }, [puzzles, selectedDifficulty]);

  const handleStartPuzzle = () => {
    const pool = filteredPuzzles.length ? filteredPuzzles : puzzles;

    if (!pool.length) {
      return;
    }

    const nextPuzzle = pool[Math.floor(Math.random() * pool.length)];
    navigate(`/play/${nextPuzzle.slug}`);
  };

  if (loading) return <p className="p-8">Loading puzzles…</p>;
  if (error) return <p className="p-8 text-red-600">Error: {error}</p>;
  if (!type) return <p className="p-8 text-red-600">Puzzle type not found.</p>;
  if (!puzzles.length) {
    return (
      <>
        <Header />
        <div className="container mx-auto p-8 space-y-3">
          <h2 className="text-2xl font-semibold text-slate-900">{type.name}</h2>
          <p className="max-w-2xl text-slate-600">
            This puzzle family is mapped in the catalog, but online play is still being prepared.
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="mx-auto max-w-6xl space-y-7 px-4 py-8">
        <section className="max-w-4xl rounded-[1.75rem] border border-white/70 bg-white/85 p-6 shadow-lg shadow-slate-200/50 sm:p-7">
          <p className="text-sm font-semibold uppercase tracking-[0.25em] text-brand-cyan">
            {type.status === 'playable' ? 'Playable now' : 'Coming soon'}
          </p>
          <h2 className="mt-3 text-3xl font-semibold text-slate-900 md:text-[2.35rem]">{type.name}</h2>
          <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-600 md:text-base">{type.description}</p>
        </section>

        <section className="rounded-[1.5rem] border border-slate-200 bg-white/85 p-5 shadow-sm">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-400">
                Start by difficulty
              </p>
              <h3 className="mt-2 text-xl font-semibold text-slate-900">
                Pick the kind of solve you want
              </h3>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
                Choose a difficulty level and jump straight into a puzzle from that set. With larger catalogs later, this becomes the quickest way to start playing.
              </p>
            </div>
            <button
              type="button"
              onClick={handleStartPuzzle}
              className="inline-flex rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-magenta"
            >
              {selectedDifficulty === 'All'
                ? 'Play a puzzle'
                : `Play a ${selectedDifficulty.toLowerCase()} puzzle`}
            </button>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setSelectedDifficulty('All')}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                selectedDifficulty === 'All'
                  ? 'bg-brand-cyan text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              All difficulties
            </button>
            {difficultyOptions.map((difficulty) => (
              <button
                key={difficulty}
                type="button"
                onClick={() => setSelectedDifficulty(difficulty)}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                  selectedDifficulty === difficulty
                    ? 'bg-brand-cyan text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {difficulty}
              </button>
            ))}
          </div>
        </section>

        <div className="grid grid-cols-1 justify-items-center gap-5 md:grid-cols-2 xl:grid-cols-3">
          {filteredPuzzles.map(({ id, title, slug, difficulty, publishDate }) => (
            <div key={id} className="w-full max-w-[18rem] rounded-[1.4rem] border border-slate-200 bg-white p-5 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                {difficulty} {publishDate ? `• ${publishDate}` : ''}
              </p>
              <h3 className="mt-3 text-xl font-semibold text-slate-900">{title}</h3>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Autosave, progress checks, and hint support are available in the browser for this puzzle.
              </p>
              <Link
                to={`/play/${slug}`}
                className="mt-4 inline-flex rounded-full bg-brand-cyan px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-magenta"
              >
                Play now
              </Link>
            </div>
          ))}
        </div>
      </main>
    </>
  );
}
